import React from "react";
import { Box } from "@mui/material";
import { Check } from "lucide-react";
import { CardShell, KEYFRAMES, MONO, OutlineButton, Spinner, StatusChip, T, ToolBadge } from "./chrome";
import { jiraConnectActions } from "./flowStore";
import { useJiraConnectFlow } from "./useJiraConnectFlow";

export default function SyncCard(): React.ReactElement {
    const flow = useJiraConnectFlow();
    const errored = flow.error?.stage === "importing";

    if (flow.stage === "synced" && flow.selectedProject) {
        return (
            <Box data-testid="jc-sync" sx={{
                fontFamily: "'Inter', sans-serif",
                background: T.bannerBg, border: `1px solid ${T.bannerBorder}`, borderRadius: "12px",
                padding: "12px 15px", display: "flex", alignItems: "center", gap: "11px",
                animation: "jcRise 0.22s cubic-bezier(0.22,0.61,0.36,1)"
            }}>
                <style>{KEYFRAMES}</style>
                <Box sx={{
                    width: 30, height: 30, borderRadius: "8px", background: T.bannerTile,
                    border: `1px solid ${T.bannerBorder}`, display: "flex", alignItems: "center",
                    justifyContent: "center", color: T.success, flex: "0 0 auto"
                }}>
                    <Check size={16} strokeWidth={2} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: "13px", fontWeight: 600, color: T.bannerTitle }}>
                        {flow.selectedProject.key} linked · {flow.syncedImported ?? 0} tickets synced
                    </Box>
                    <Box sx={{ fontSize: "12px", color: T.bannerSub, marginTop: "1px" }}>
                        Available in the Context Mesh
                    </Box>
                </Box>
            </Box>
        );
    }

    const importing = flow.stage === "importing";
    const progress = flow.syncProgress;
    const pct = progress && progress.total > 0 ? (progress.imported / progress.total) * 100 : 4;

    return (
        <CardShell testId="jc-sync">
            <Box sx={{ padding: "13px 14px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <ToolBadge name="integration_sync" />
                    {errored ? (
                        <StatusChip tone="error" label="Import failed" />
                    ) : importing ? (
                        <Box sx={{ marginLeft: "auto" }}><Spinner /></Box>
                    ) : (
                        <StatusChip tone="neutral" label="Waiting on project link" />
                    )}
                </Box>
                {errored ? (
                    <>
                        <Box sx={{ fontSize: "12px", color: T.fg3, marginBottom: "6px" }}>{flow.error?.message}</Box>
                        {progress && progress.total > 0 && (
                            <Box sx={{ fontSize: "11.5px", color: T.fg4, fontFamily: MONO, marginBottom: "10px" }}>
                                Import stopped at {progress.imported} / {progress.total}
                            </Box>
                        )}
                        <OutlineButton label="Retry" onClick={jiraConnectActions.retry} />
                    </>
                ) : importing ? (
                    <>
                        <Box sx={{ fontSize: "13px", fontWeight: 700, color: T.indigo700, marginBottom: "9px" }}>
                            Importing tickets…
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                            <Box sx={{ flex: 1, height: "6px", borderRadius: "999px", background: T.slate200, overflow: "hidden" }}>
                                <Box
                                    data-testid="jc-sync-fill"
                                    style={{ width: `${pct}%` }}
                                    sx={{ height: "100%", borderRadius: "999px", background: T.primary, transition: "width 0.3s ease" }}
                                />
                            </Box>
                            {progress && progress.total > 0 && (
                                <Box sx={{ fontSize: "11.5px", fontWeight: 600, color: T.primary, fontFamily: MONO, flex: "0 0 auto" }}>
                                    {progress.imported} / {progress.total}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ fontSize: "11.5px", color: T.fg4 }}>Adding tickets to the Context Mesh</Box>
                    </>
                ) : (
                    <Box sx={{ fontSize: "12.5px", color: T.fg3, lineHeight: 1.5 }}>
                        Once the project is linked, its tickets are imported into the Context Mesh to complete the connection.
                    </Box>
                )}
            </Box>
        </CardShell>
    );
}
